import { useState } from "react";
import type { Unit } from "../../types/company";
import { BLOCKING_STATUSES, TODAY } from "../../data/company";
import { driverName, findDriver, listingName, unitsOf } from "../../lib/company";
import { useCompany } from "../../lib/companyContext";
import { cn } from "../../lib/format";
import { BlockDatesModal } from "../../components/company/modals";
import { PageTitle } from "../../components/company/ui";
import { useT } from "../../i18n";

const LEGEND = [
  ["bg-[#BFDCC8]", "confirmed"],
  ["bg-[#C5D4F6]", "active"],
  ["bg-[#D6D7D8]", "blocked"],
  ["bg-[#F6E3C0]", "maintenance"],
] as const;

const navButton =
  "h-9 w-9 cursor-pointer rounded border-0 bg-white text-lg text-ink hover:bg-[#FAFAFA]";

export default function CompanyCalendar() {
  const t = useT();
  const cal = t.company.calendar;
  const { state } = useCompany();
  const [offset, setOffset] = useState(0);
  const [blockUnit, setBlockUnit] = useState<Unit | null>(null);

  const [ty, tm] = TODAY.split("-").map(Number);
  const base = new Date(Date.UTC(ty, tm - 1 + offset, 1));
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth();
  const dayCount = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const days = Array.from({ length: dayCount }, (_, i) => {
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
    const weekday = new Date(Date.UTC(year, month, i + 1)).getUTCDay();
    return { iso, n: i + 1, today: iso === TODAY, weekend: weekday === 0 || weekday === 6 };
  });

  const cell = (u: Unit, iso: string): { className: string; title: string } => {
    const b = state.bookings.find(
      (x) => x.unitId === u.id && BLOCKING_STATUSES.includes(x.status) && x.start <= iso && iso < x.end,
    );
    if (b) {
      return {
        className: b.status === "active" ? "bg-[#C5D4F6]" : "bg-[#BFDCC8]",
        title: `${b.id} · ${driverName(findDriver(state, b.driverId))}`,
      };
    }
    const block = u.blocks.find((x) => x.start <= iso && iso < x.end);
    if (block) return { className: "bg-[#D6D7D8]", title: block.reason || cal.legend.blocked };
    if (u.status === "maintenance") return { className: "bg-[#F6E3C0]", title: cal.legend.maintenance };
    return { className: "", title: iso };
  };

  const groups = state.listings
    .filter((l) => l.publish !== "archived")
    .map((l) => ({ listing: l, units: unitsOf(state, l.id) }))
    .filter((g) => g.units.length);
  const unassigned = state.bookings.filter((b) => b.status === "pending").length;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <PageTitle className="mb-0">{cal.title}</PageTitle>
        <div className="flex items-center gap-1.5">
          <button type="button" aria-label={cal.previous} onClick={() => setOffset((o) => o - 1)} className={navButton}>
            ‹
          </button>
          <span className="min-w-32.5 text-center font-medium">
            {cal.months[month]} {year}
          </span>
          <button type="button" aria-label={cal.next} onClick={() => setOffset((o) => o + 1)} className={navButton}>
            ›
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-4 text-xs text-muted">
        {LEGEND.map(([color, key]) => (
          <span key={key} className="flex items-center gap-1.5">
            <span className={cn("h-2.5 w-3.5 rounded-xs", color)} />
            {cal.legend[key]}
          </span>
        ))}
        <span>{cal.hint}</span>
      </div>

      <div className="overflow-x-auto rounded-card bg-white">
        <div style={{ minWidth: 150 + dayCount * 28 }}>
          <div className="flex border-b border-line">
            <div className="w-37.5 shrink-0 px-3 py-2 text-xs text-faint">{cal.vehicle}</div>
            {days.map((d) => (
              <div
                key={d.iso}
                className={cn(
                  "w-7 shrink-0 py-2 text-center text-[11px]",
                  d.today ? "bg-tone-blue-bg font-semibold text-brand-link" : d.weekend ? "bg-[#FAFAFA] text-muted" : "text-muted",
                )}
              >
                {d.n}
              </div>
            ))}
          </div>

          {groups.map(({ listing, units }) => (
            <div key={listing.id}>
              <div className="border-b border-[#F0F0F0] bg-[#FAFAFA] px-3 py-2 text-xs font-medium">
                {listingName(listing)} · {listing.city}
              </div>
              {units.map((u) => (
                <div key={u.id} className="flex border-b border-surface">
                  <button
                    type="button"
                    onClick={() => setBlockUnit(u)}
                    className="w-37.5 shrink-0 cursor-pointer border-0 bg-white px-3 py-1.5 text-left font-mono text-xs font-medium text-ink hover:bg-[#FAFAFA]"
                  >
                    {u.code}
                  </button>
                  {days.map((d) => {
                    const c = cell(u, d.iso);
                    return (
                      <div
                        key={d.iso}
                        title={c.title}
                        className={cn("h-7.5 w-7 shrink-0 border-l border-[#F7F7F7]", c.className)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
          {groups.length === 0 && (
            <p className="m-0 px-6 py-12 text-center text-sm text-muted">{cal.empty}</p>
          )}
        </div>
      </div>

      {unassigned > 0 && (
        <p className="mt-3 mb-0 text-[13px] text-muted">
          {cal.unassigned(unassigned)}
        </p>
      )}

      {blockUnit && <BlockDatesModal unit={blockUnit} onClose={() => setBlockUnit(null)} />}
    </>
  );
}
